import React, { useState } from 'react';
import { Users, Plus, Edit2, Trash2, Save, X, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

const GroupsSection = ({ 
  formData, 
  handleInputChange, 
  handleArrayChange,
  errors 
}) => {
  // Состояние формы для добавления/редактирования группы
  const [groupForm, setGroupForm] = useState({
    id: null,
    name: '',
    size: 25
  });
  
  // Флаг, показывающий, находимся ли мы в режиме редактирования
  const [isEditing, setIsEditing] = useState(false);
  
  // Функция сброса формы
  const resetForm = () => {
    setGroupForm({
      id: null,
      name: '',
      size: 25
    });
    setIsEditing(false);
  };
  
  // Функция для обработки изменений в форме группы
  const handleGroupFormChange = (e) => {
    const { name, value } = e.target;
    setGroupForm(prev => ({
      ...prev,
      [name]: name === 'size' ? parseInt(value) || 1 : value
    }));
  };
  
  // Функция для добавления/обновления группы
  const handleSubmitGroup = (e) => {
    e.preventDefault();
    
    // Проверка заполнения названия
    if (!groupForm.name.trim()) {
      alert('Пожалуйста, введите название группы или класса');
      return;
    }
    
    // Проверка размера группы
    if (groupForm.size < 1 || groupForm.size > 150) {
      alert('Размер группы должен быть от 1 до 150 человек');
      return;
    }
    
    if (isEditing) {
      // Обновление существующей группы
      handleArrayChange('groups', 'update', groupForm);
    } else {
      // Добавление новой группы
      handleArrayChange('groups', 'add', groupForm);
    }
    
    // Сброс формы
    resetForm();
  };
  
  // Функция для начала редактирования группы
  const startEditingGroup = (group) => {
    setGroupForm({
      id: group.id,
      name: group.name,
      size: group.size
    });
    setIsEditing(true);
  };
  
  // Функция для удаления группы
  const deleteGroup = (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту группу/класс?')) {
      handleArrayChange('groups', 'remove', { id });
    }
  };
  
  // Шаблоны типовых групп для быстрого добавления
  const groupTemplates = [
    { name: 'Добавить все классы с 1 по 11', action: 'add_all_classes' },
    { name: 'Добавить классы с литерами А, Б, В', action: 'add_classes_with_letters' },
    { name: 'Добавить группы вуза (101-105)', action: 'add_university_groups' }
  ];
  
  // Функция для обработки выбора шаблона групп
  const handleTemplateAction = (action) => {
    switch (action) {
      case 'add_all_classes':
        for (let i = 1; i <= 11; i++) {
          handleArrayChange('groups', 'add', {
            name: `${i} класс`,
            size: i <= 4 ? 25 : (i <= 9 ? 30 : 25) // Обычно старшие классы меньше
          });
        }
        break;
      case 'add_classes_with_letters':
        for (let i = 1; i <= 11; i++) {
          for (let letter of ['А', 'Б', 'В']) {
            handleArrayChange('groups', 'add', {
              name: `${i}${letter}`,
              size: i <= 4 ? 25 : (i <= 9 ? 30 : 25)
            });
          }
        }
        break;
      case 'add_university_groups':
        for (let i = 101; i <= 105; i++) {
          handleArrayChange('groups', 'add', {
            name: `Группа ${i}`,
            size: 25
          });
        }
        break;
      default:
        break;
    }
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium text-gray-900 dark:text-white flex items-center">
        <Users className="w-5 h-5 mr-2 text-indigo-500" />
        Группы/Классы
      </h3>
      
      {/* Форма добавления/редактирования группы */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">
          {isEditing ? 'Редактирование группы/класса' : 'Добавление новой группы/класса'}
        </h4>
        
        <form onSubmit={handleSubmitGroup} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Название группы/класса
              </label>
              <input
                type="text"
                id="groupName"
                name="name"
                value={groupForm.name}
                onChange={handleGroupFormChange}
                className="form-input w-full"
                placeholder="Например: 11А, Группа 101"
                required
              />
            </div>
            
            <div>
              <label htmlFor="groupSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Количество учащихся
              </label>
              <input
                type="number"
                id="groupSize"
                name="size"
                min="1"
                max="150"
                value={groupForm.size}
                onChange={handleGroupFormChange}
                className="form-input w-full"
                required
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              primary
              size="sm"
              icon={isEditing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            >
              {isEditing ? 'Сохранить' : 'Добавить группу/класс'}
            </Button>
            
            {isEditing && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={<X className="w-4 h-4" />}
                onClick={resetForm}
              >
                Отменить
              </Button>
            )}
          </div>
        </form>
      </div>
      
      {/* Шаблоны групп */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <h4 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-3">
          Быстрое добавление групп
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {groupTemplates.map((template, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleTemplateAction(template.action)}
              className="text-left p-2 rounded-md border border-indigo-200 dark:border-indigo-700 bg-white dark:bg-gray-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors text-sm text-indigo-700 dark:text-indigo-300"
            >
              {template.name}
            </button>
          ))}
        </div>
        
        <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2">
          <Info className="w-4 h-4 inline-block mr-1" />
          Эти шаблоны помогут быстро добавить типовые группы или классы. Вы всегда сможете отредактировать их позже.
        </p>
      </div>
      
      {/* Список групп */}
      <div>
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">Список групп/классов</h4>
        
        {errors.groups && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.groups}
          </div>
        )}
        
        {formData.groups.length === 0 ? (
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">Группы/классы не добавлены</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => document.getElementById('groupName').focus()}
            >
              Добавить первую группу/класс
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {formData.groups.map((group) => (
              <div 
                key={group.id} 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center">
                  <div className="flex-grow">
                    <div className="font-medium text-gray-800 dark:text-gray-200">
                      {group.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {group.size} {pluralize(group.size, 'учащийся', 'учащихся', 'учащихся')}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => startEditingGroup(group)}
                      className="text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                      title="Редактировать"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteGroup(group.id)}
                      className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Информация */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Рекомендации по наполнению классов
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              По нормативам СанПиН, в школах России наполняемость классов не должна превышать 25 человек в городских школах 
              и 14 человек в сельских. Для ВУЗов оптимальной считается наполняемость групп в 25-30 человек для лекционных занятий 
              и 12-15 человек для практических и лабораторных работ.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return many;
  }
  
  if (lastDigit === 1) {
    return one;
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return few;
  }
  
  return many;
};

export default GroupsSection;